import { useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Activity, AlertTriangle, Building2, Calendar, CalendarCheck, Car, FileText, Inbox, Key,
  KeyRound, LayoutDashboard, LifeBuoy, List, LogOut, Megaphone, Menu, Settings as SettingsIcon, Shield,
  Receipt, Sparkles, Star, UserCog, Users, Wrench, X, ClipboardList,
} from "lucide-react";
import { useAuthStore } from "../store/authStore";
import { useSchema } from "../lib/schema";

// The registry names an icon; this maps it to the component. Anything
// unrecognised falls back to a generic list icon rather than breaking the nav.
const ICONS: Record<string, typeof List> = {
  "calendar-check": CalendarCheck, calendar: Calendar, sparkles: Sparkles,
  "clipboard-list": ClipboardList, "file-text": FileText, star: Star, wrench: Wrench,
  "building-2": Building2, list: List, megaphone: Megaphone, users: Users,
  "user-cog": UserCog, car: Car, "key-round": KeyRound, inbox: Inbox, shield: Shield, key: Key,
  receipt: Receipt, "life-buoy": LifeBuoy,
};

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, signOut } = useAuthStore();
  const { data } = useSchema();
  const navigate = useNavigate();

  const groups = useMemo(() => {
    const byGroup = new Map<string, { key: string; label: string; icon?: string }[]>();
    for (const r of data?.resources || []) {
      if (!byGroup.has(r.group)) byGroup.set(r.group, []);
      byGroup.get(r.group)!.push({ key: r.key, label: r.label, icon: r.icon });
    }
    return Array.from(byGroup.entries());
  }, [data]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors ${
      isActive ? "bg-ink-800 text-white" : "text-ink-400 hover:bg-ink-850 hover:text-ink-200"
    }`;

  return (
    <div className="min-h-screen bg-ink-950">
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-60 border-r border-ink-800 bg-ink-900 transition-transform lg:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-ink-800 px-4 py-3.5">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-accent" />
              <span className="text-sm font-semibold tracking-tight text-white">Console</span>
            </div>
            <button className="lg:hidden text-ink-400" onClick={() => setMobileOpen(false)}>
              <X className="h-4 w-4" />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-2 py-3" onClick={() => setMobileOpen(false)}>
            <div className="space-y-0.5">
              <NavLink to="/" end className={linkClass}>
                <LayoutDashboard className="h-4 w-4 shrink-0" /> Översikt
              </NavLink>
              <NavLink to="/activity" className={linkClass}>
                <Activity className="h-4 w-4 shrink-0" /> Aktivitet
              </NavLink>
            </div>

            {groups.map(([group, items]) => (
              <div key={group} className="mt-5">
                <div className="px-2.5 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-600">
                  {group}
                </div>
                <div className="space-y-0.5">
                  {items.map((item) => {
                    const Icon = ICONS[item.icon || ""] || List;
                    return (
                      <NavLink key={item.key} to={`/r/${item.key}`} className={linkClass}>
                        <Icon className="h-4 w-4 shrink-0" />
                        <span className="truncate">{item.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="mt-5">
              <div className="px-2.5 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-600">
                Plattform
              </div>
              <NavLink to="/settings" className={linkClass}>
                <SettingsIcon className="h-4 w-4 shrink-0" /> Inställningar
              </NavLink>
            </div>
          </nav>

          <div className="border-t border-ink-800 p-3">
            <div className="mb-2 px-1">
              <div className="truncate text-xs font-medium text-ink-300">{user?.name || user?.email}</div>
              <div className="flex items-center gap-1 text-[10px] text-ink-500">
                <AlertTriangle className="h-3 w-3" /> Superadmin — allt loggas
              </div>
            </div>
            <button onClick={handleSignOut} className="btn-ghost w-full justify-center">
              <LogOut className="h-3.5 w-3.5" /> Logga ut
            </button>
          </div>
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-30 bg-black/60 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      <div className="lg:pl-60">
        <button
          className="fixed left-3 top-3 z-20 rounded-md border border-ink-700 bg-ink-900 p-2 text-ink-300 lg:hidden"
          onClick={() => setMobileOpen(true)}
        >
          <Menu className="h-4 w-4" />
        </button>
        <main className="p-4 pt-14 lg:p-6 lg:pt-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
